import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useStore } from '../store';
import { newId } from '../dataFile';
import { renderChallanPdf } from './pdfRenderer';
import ChallanForm from './ChallanForm';
import ChallanPreview from './ChallanPreview';
import HistorySidebar from './HistorySidebar';

function todayStr() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function emptyRow() {
  return { id: newId(), number: '', sr_no: '', brand: '', description: '', quantity: '1' };
}

function emptyForm(counter) {
  return {
    challanNo: counter,
    dateStr: todayStr(),
    clientId: null,
    custom: { name: '', lines: ['', '', '', ''], gst: '' },
    rows: [emptyRow()],
  };
}

function resolveCustomer(form, clients) {
  if (form.clientId) {
    const c = clients.find((x) => x.id === form.clientId);
    if (c) return { customerName: c.name, customerLines: c.lines, customerGst: c.gst };
  }
  return {
    customerName: form.custom.name,
    customerLines: form.custom.lines.filter(Boolean),
    customerGst: form.custom.gst,
  };
}

function totalOf(rows) {
  let t = 0;
  for (const r of rows) {
    const q = parseInt(r.quantity, 10);
    if (!Number.isNaN(q)) t += q;
  }
  return t;
}

export default function ChallanTool() {
  const { data, updateData, commitAndSave } = useStore();

  // viewingHistoryId: when set, the form is showing a previously-issued challan in
  // read-only/template mode. Generate is disabled; user can re-download the PDF or
  // start a new challan.
  const [viewingHistoryId, setViewingHistoryId] = useState(null);
  const [showHistory, setShowHistory] = useState(true);

  // Initialize form: prefer saved draft, else empty seeded with counter.
  const [form, setForm] = useState(() => {
    return data.drafts?.challan || emptyForm(data.counter);
  });

  // Mirror form into drafts on every change (auto-save draft) — only when editing
  // a new challan (not when viewing history).
  useEffect(() => {
    if (viewingHistoryId) return;
    updateData((d) => ({ ...d, drafts: { ...(d.drafts || {}), challan: form } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, viewingHistoryId]);

  // Build payload for the PDF renderer
  const payload = useMemo(() => {
    const cust = resolveCustomer(form, data.clients);
    return {
      company: data.company,
      challanNo: form.challanNo,
      dateStr: form.dateStr,
      ...cust,
      rows: form.rows,
    };
  }, [form, data.clients, data.company]);

  // Debounced PDF preview generation
  const [previewBytes, setPreviewBytes] = useState(null);
  const renderTimer = useRef(null);
  useEffect(() => {
    if (renderTimer.current) clearTimeout(renderTimer.current);
    renderTimer.current = setTimeout(async () => {
      try {
        const bytes = await renderChallanPdf(payload);
        setPreviewBytes(bytes);
      } catch (e) {
        console.error('preview render failed', e);
      }
    }, 300);
    return () => clearTimeout(renderTimer.current);
  }, [payload]);

  const downloadCurrent = useCallback(async () => {
    const bytes = await renderChallanPdf(payload);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.challanNo}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [payload, form.challanNo]);

  const generate = useCallback(async () => {
    if (viewingHistoryId) {
      alert('Click "New challan" first to generate a fresh one.');
      return;
    }
    if (!form.clientId && !form.custom.name.trim()) {
      alert('Pick a client or enter a custom name first.');
      return;
    }
    const validRows = form.rows.filter(
      (r) => r.number || r.sr_no || r.brand || r.description
    );
    if (!validRows.length) {
      alert('Add at least one line item.');
      return;
    }

    // Render & download the PDF first
    await downloadCurrent();

    // Build history entry
    const cust = resolveCustomer(form, data.clients);
    const entry = {
      id: newId(),
      challanNo: form.challanNo,
      dateStr: form.dateStr,
      customerName: cust.customerName || '(unnamed)',
      total: totalOf(form.rows),
      snapshot: form,
      generatedAt: new Date().toISOString(),
    };

    const nextData = {
      ...data,
      counter: Number(form.challanNo) + 1,
      lastChallan: form,
      drafts: { ...(data.drafts || {}), challan: null },
      history: [entry, ...(data.history || [])],
    };

    // Atomically: update state + auto-download fresh data file (so counter is persisted)
    commitAndSave(nextData);

    // Reset to a fresh form for the next challan
    setForm(emptyForm(nextData.counter));
  }, [form, data, downloadCurrent, commitAndSave, viewingHistoryId]);

  const duplicateFromPrevious = useCallback(() => {
    if (!data.lastChallan) {
      alert('No previous challan to duplicate.');
      return;
    }
    const dup = {
      ...data.lastChallan,
      challanNo: data.counter,
      dateStr: todayStr(),
      rows: data.lastChallan.rows.map((r) => ({ ...r, id: newId() })),
    };
    setViewingHistoryId(null);
    setForm(dup);
  }, [data.lastChallan, data.counter]);

  const addClient = useCallback((client) => {
    const withId = { ...client, id: newId() };
    updateData((d) => ({ ...d, clients: [...d.clients, withId] }));
    setForm((f) => ({ ...f, clientId: withId.id }));
  }, [updateData]);

  const deleteClient = useCallback((clientId) => {
    updateData((d) => ({ ...d, clients: d.clients.filter((c) => c.id !== clientId) }));
    setForm((f) => (f.clientId === clientId ? { ...f, clientId: null } : f));
  }, [updateData]);

  const updateCompany = useCallback((patch) => {
    updateData((d) => ({ ...d, company: { ...(d.company || {}), ...patch } }));
  }, [updateData]);

  const loadFromHistory = useCallback((entry) => {
    if (!entry?.snapshot) return;
    setViewingHistoryId(entry.id);
    setForm(entry.snapshot);
  }, []);

  const newChallan = useCallback(() => {
    setViewingHistoryId(null);
    setForm(emptyForm(data.counter));
  }, [data.counter]);

  const deleteHistoryEntry = useCallback((entryId) => {
    if (!window.confirm('Remove this challan from history? The PDF you already saved is unaffected.')) return;
    updateData((d) => ({ ...d, history: (d.history || []).filter((e) => e.id !== entryId) }));
    if (viewingHistoryId === entryId) {
      setViewingHistoryId(null);
      setForm(emptyForm(data.counter));
    }
  }, [updateData, viewingHistoryId, data.counter]);

  return (
    <div className="flex gap-3 h-[calc(100vh-7rem)]">
      {showHistory && (
        <div className="w-64 flex-shrink-0">
          <HistorySidebar
            history={data.history || []}
            activeId={viewingHistoryId}
            onPick={loadFromHistory}
            onDelete={deleteHistoryEntry}
            onNew={newChallan}
            onClose={() => setShowHistory(false)}
          />
        </div>
      )}
      {!showHistory && (
        <button className="btn btn-sm btn-outline self-start"
                onClick={() => setShowHistory(true)} title="Show history">
          ☰
        </button>
      )}

      <div className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-4 min-w-0">
        <div className="bg-base-200 rounded-lg overflow-hidden">
          <ChallanPreview bytes={previewBytes} />
        </div>
        <div className="overflow-auto pr-2">
          {viewingHistoryId && (
            <div className="alert alert-info mb-3 py-2 text-sm flex-wrap gap-2">
              <span>Viewing a previously-generated challan (read-only).</span>
              <div className="flex gap-1">
                <button className="btn btn-xs" onClick={downloadCurrent}>Download PDF</button>
                <button className="btn btn-xs btn-primary" onClick={newChallan}>New challan</button>
              </div>
            </div>
          )}
          <ChallanForm
            form={form}
            setForm={setForm}
            clients={data.clients}
            company={data.company || {}}
            onUpdateCompany={updateCompany}
            onAddClient={addClient}
            onDeleteClient={deleteClient}
            onGenerate={generate}
            onDownloadPreview={downloadCurrent}
            onDuplicate={duplicateFromPrevious}
            hasPrevious={!!data.lastChallan}
            readOnly={!!viewingHistoryId}
          />
        </div>
      </div>
    </div>
  );
}
